/**
 * Cart lines as stored in the browser (`localStorage['vs-carrello']`) and sent to
 * `validaCarrello`. A line only says *what* the customer wants: prices, names and availability
 * always come back from the server. Shared by client and server, so no server-only imports.
 */

export const CHIAVE_STORAGE = 'vs-carrello'

export const QUANTITA_MAX = 99
export const RIGHE_MAX = 50
const TESTO_MAX = 80

/**
 * A paint typed by hand, for when it isn't (yet) in the `tinte` collection. Like a paint picked
 * in the colour finder, it always carries the vehicle model it is for.
 */
export type TintaLibera = {
  marca: string
  codice: string
  veicolo: string
}

export type RigaCarrello = {
  sku: string
  quantita: number
  /** `tinte` id, for made-to-measure products. */
  tinta?: number
  /** Vehicle model the `tinta` was picked for (`?modello=` on the paint page), as text. */
  veicolo?: string
  tintaLibera?: TintaLibera
}

const testo = (value: unknown, max = TESTO_MAX) =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').slice(0, max) : ''

/**
 * Identity of a line: two lines with the same key are the same thing and get merged. Paint
 * text is compared case-insensitively, so "Fiat 199" and "FIAT 199" end up on one line.
 */
export const chiaveRiga = ({ sku, tinta, veicolo, tintaLibera }: Omit<RigaCarrello, 'quantita'>) =>
  JSON.stringify([
    sku,
    tinta ?? null,
    tinta && veicolo ? veicolo.toLowerCase() : null,
    tintaLibera
      ? [tintaLibera.marca, tintaLibera.codice, tintaLibera.veicolo].map((s) => s.toLowerCase())
      : null,
  ])

/**
 * Turns untrusted input (localStorage, the server action's argument) into well-formed lines.
 * Unknown keys — a `prezzoCentesimi`, say — are dropped. Returns the lines it had to discard
 * separately, so the server can report them.
 */
export const normalizzaRighe = (input: unknown): { righe: RigaCarrello[]; scartate: unknown[] } => {
  const righe: RigaCarrello[] = []
  const scartate: unknown[] = []
  if (!Array.isArray(input)) return { righe, scartate }

  for (const raw of input) {
    if (!raw || typeof raw !== 'object') {
      scartate.push(raw)
      continue
    }
    const r = raw as Record<string, unknown>
    const sku = testo(r.sku, 64)
    const quantita = Number(r.quantita)
    if (!sku || !Number.isSafeInteger(quantita) || quantita < 1) {
      scartate.push(raw)
      continue
    }

    const riga: RigaCarrello = { sku, quantita: Math.min(quantita, QUANTITA_MAX) }

    if (typeof r.tinta === 'number' && Number.isSafeInteger(r.tinta) && r.tinta > 0) {
      riga.tinta = r.tinta
      const veicolo = testo(r.veicolo)
      if (veicolo) riga.veicolo = veicolo
    } else if (r.tintaLibera && typeof r.tintaLibera === 'object') {
      // An incomplete paint (no brand, code or model, e.g. a line saved before the model was
      // required) is dropped but the line is kept: `validaCarrello` then reports the product
      // as missing its paint, by name, instead of a nameless invalid line.
      const t = r.tintaLibera as Record<string, unknown>
      const marca = testo(t.marca)
      const codice = testo(t.codice)
      const veicolo = testo(t.veicolo)
      if (marca && codice && veicolo) riga.tintaLibera = { marca, codice, veicolo }
    }

    righe.push(riga)
  }

  return { righe: unisciRighe(righe), scartate }
}

/** Merges identical lines (same key) by adding up their quantities. Order is preserved. */
export const unisciRighe = (righe: RigaCarrello[]): RigaCarrello[] => {
  const perChiave = new Map<string, RigaCarrello>()
  for (const riga of righe) {
    const chiave = chiaveRiga(riga)
    const esistente = perChiave.get(chiave)
    if (esistente) {
      esistente.quantita = Math.min(esistente.quantita + riga.quantita, QUANTITA_MAX)
    } else {
      perChiave.set(chiave, { ...riga })
    }
  }
  return [...perChiave.values()].slice(0, RIGHE_MAX)
}
